import * as methods from 'api/core/actions';
import { omit, uniq } from 'lodash-es';
import type {
  DaySummary,
  GroupedSummaryDuration,
  RawUserSummary,
  RawUserSummaryGrouped,
} from 'redux/modules/summaries.types';
import type {
  HoursPerWeekSummary,
  UsersSummaryQuery,
  UsersSummaryTotals,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import type { Duration } from 'types/units';
import { durationFromHours } from 'utils/durations';
import { keyBy, parseZodType } from 'utils/typesafe';
import * as z from 'zod';

const avatarSchema = z.object({
  large_retina: z.string(),
  large: z.string(),
  medium_retina: z.string(),
  medium: z.string(),
});
const durationSchema = z.object({
  hours: z.number(),
  minutes: z.number(),
  seconds: z.number(),
  formatted: z.string(),
  total_hours: z.number(),
  total_seconds: z.number(),
  total_minutes: z.number(),
});
const shared = {
  id: z.number(),
  email: z.string(),
  name: z.string(),
  updated_at: z.string(),
  avatar: avatarSchema,
  total_capacity: durationSchema,
};

const defaultDurations = {
  duration: durationSchema,
  billable_duration: durationSchema,
  non_billable_duration: durationSchema,
  billable_estimated_duration: durationSchema,
  non_billable_estimated_duration: durationSchema,
  total_duration: durationSchema,
};
const defaultDurationsSchema = z.object(defaultDurations);

const forecastDurations = {
  assigned_task_duration: z.optional(durationSchema),
  estimated_duration: z.optional(durationSchema),
  logged_duration: z.optional(durationSchema),
  planned_duration: z.optional(durationSchema),
  overtime_duration: z.optional(durationSchema),
};
const forecastDurationsSchema = z.object(forecastDurations);

const byDayShared = {
  day: z.string(),
  capacity: durationSchema,
};

const byDayDefaultSchema = z.object({
  ...byDayShared,
  ...defaultDurations,
});

const byDayForecastSchema = z.object({
  ...byDayShared,
  ...forecastDurations,
});

const userSummaryGroupShared = {
  since: z.string(),
  until: z.string(),
  capacity: durationSchema,
};

const userSummaryDefaultGroupedSchema = z.object({
  ...shared,
  ...defaultDurations,
  weekly_capacity: durationSchema,
  total_logged_percent: z.optional(z.number()),
  by_days: z.optional(z.array(byDayDefaultSchema)),
  grouped: z.array(
    z.object({
      ...userSummaryGroupShared,
      ...defaultDurations,
    })
  ),
  access_all_hours: z.boolean(),
});

const userSummaryForecastGroupedSchema = z.object({
  ...shared,
  ...forecastDurations,
  by_days: z.optional(z.array(byDayForecastSchema)),
  grouped: z.array(
    z.object({
      ...userSummaryGroupShared,
      ...forecastDurations,
    })
  ),
});
const userSummaryDefaultSchema = userSummaryDefaultGroupedSchema.omit({ grouped: true });
const userSummaryForecastSchema = userSummaryForecastGroupedSchema.omit({ grouped: true });

const userSummaryDefaultGroupedTotalsSchema = z.object({
  users: z.number(),
  capacity: durationSchema,
  ...defaultDurations,
  grouped: z.optional(
    z.array(
      z.object({
        since: z.string(),
        until: z.string(),
        ...defaultDurations,
      })
    )
  ),
});
const userSummaryForecastGroupedTotalsSchema = z.object({
  users: z.number(),
  capacity: durationSchema,
  ...forecastDurations,
  grouped: z.optional(
    z.array(
      z.object({
        since: z.string(),
        until: z.string(),
        ...forecastDurations,
      })
    )
  ),
});

export type UserSummaryDefaultPayload = z.infer<typeof userSummaryDefaultSchema>;
export type UserSummaryDefaultGroupedPayload = z.infer<typeof userSummaryDefaultGroupedSchema>;
type UserSummaryDefaultGroupedTotalsPayload = z.infer<typeof userSummaryDefaultGroupedTotalsSchema>;
export type UserSummaryForecastPayload = z.infer<typeof userSummaryForecastSchema>;
export type UserSummaryForecastGroupedPayload = z.infer<typeof userSummaryForecastGroupedSchema>;
type UserSummaryForecastGroupedTotalsPayload = z.infer<
  typeof userSummaryForecastGroupedTotalsSchema
>;

const getParams = (props: Omit<UsersSummaryQuery, 'hasAccessToTasks'>) => {
  const { perPage, userIds, teamIds, groupBy } = props;

  return {
    since: props.since,
    until: props.until,
    page: props.page,
    percent: props.percent,
    per_page: perPage,
    user_ids: userIds,
    team_ids: teamIds,
    group_by: groupBy,
    filter: props.filter,
    timeframe_filter_method: props.timeframe_filter_method,
  };
};

export async function getUsersSummaryTotals(props: UsersSummaryQuery): Promise<UsersSummaryTotals> {
  const { hasAccessToTasks } = props;
  const params = {
    ...getParams(props),
    totals: true,
  };

  const [summary, forecastSummary] = await Promise.all([
    methods.Post('users/summary', params),
    hasAccessToTasks ? methods.Post('users/summary', { ...params, scope: 'forecasts' }) : undefined,
  ]);

  const summaryValidated = parseZodType(userSummaryDefaultGroupedTotalsSchema, summary);
  const forecastSummaryValidated = forecastSummary
    ? parseZodType(userSummaryForecastGroupedTotalsSchema, forecastSummary)
    : undefined;

  return mergeTotalsWithGroups(summaryValidated, forecastSummaryValidated);
}

export async function getUsersSummary(
  props: Omit<UsersSummaryQuery, 'hasAccessToTasks'>,
  scope: 'forecasts' | null = null
): Promise<Array<UserSummaryDefaultPayload> | Array<UserSummaryForecastPayload>> {
  const params = {
    ...getParams(props),
    scope,
    totals: null,
  };

  const summaries = await methods.Post('users/summary', params);

  const validator = scope === 'forecasts' ? userSummaryForecastSchema : userSummaryDefaultSchema;
  const summariesValidated = parseZodType(z.array(validator), summaries);

  return summariesValidated;
}

export async function getUsersSummaryGrouped(
  props: UsersSummaryQuery,
  scope: 'forecasts' | null = null
): Promise<Array<UserSummaryDefaultGroupedPayload> | Array<UserSummaryForecastGroupedPayload>> {
  const params = {
    ...getParams(props),
    scope,
    totals: null,
  };

  const summaries = await methods.Post('users/summary', params);

  const validator =
    scope === 'forecasts' ? userSummaryForecastGroupedSchema : userSummaryDefaultGroupedSchema;
  const summariesValidated = parseZodType(z.array(validator), summaries);

  return summariesValidated;
}

const baseDurations = {
  duration: durationFromHours(0),
  billable_duration: durationFromHours(0),
  non_billable_duration: durationFromHours(0),
  total_duration: durationFromHours(0),
  planned_duration: durationFromHours(0),
  tasked_duration: durationFromHours(0),
};
export function mergeSummariesWithGroups(
  summary: UserSummaryDefaultGroupedPayload | undefined,
  forecastSummary: UserSummaryForecastGroupedPayload | undefined = undefined
): RawUserSummaryGrouped {
  return {
    ...mergeSummaries(summary!, forecastSummary!),
    grouped: mergeGrouped(summary?.grouped || [], forecastSummary?.grouped || []),
  };
}

function mergeGrouped(
  defaultGroups: UserSummaryDefaultGroupedPayload['grouped'],
  forecastGroups: UserSummaryForecastGroupedPayload['grouped']
): Array<GroupedSummaryDuration> {
  const grouped = keyBy(defaultGroups, 'since');
  const forecastGrouped = keyBy(forecastGroups, 'since');
  const keys = uniq(Object.keys(grouped).concat(Object.keys(forecastGrouped)));
  const merged = keys.map(
    (key): GroupedSummaryDuration => ({
      ...baseDurations,
      ...massageDefaultDurations(grouped[key]),
      ...massageForecastDurations(forecastGrouped[key]),
    })
  );
  return merged;
}

function mergeTotalsWithGroups(
  summary: UserSummaryDefaultGroupedTotalsPayload | undefined,
  forecastSummary: UserSummaryForecastGroupedTotalsPayload | undefined
): UsersSummaryTotals {
  return {
    ...baseDurations,
    ...massageDefaultDurations(summary),
    ...massageForecastDurations(forecastSummary),
    grouped: mergeGroupedTotals(summary?.grouped || [], forecastSummary?.grouped || []),
  };
}

function mergeGroupedTotals(
  defaultGroups: UserSummaryDefaultGroupedTotalsPayload['grouped'],
  forecastGroups: UserSummaryForecastGroupedTotalsPayload['grouped']
): Array<HoursPerWeekSummary> {
  const grouped = keyBy(defaultGroups, 'since');
  const forecastGrouped = keyBy(forecastGroups, 'since');
  const keys = uniq(Object.keys(grouped).concat(Object.keys(forecastGrouped)));
  const merged = keys.map(
    (key): HoursPerWeekSummary => ({
      capacity: durationFromHours(0),
      ...baseDurations,
      ...massageDefaultDurations(grouped[key]),
      ...massageForecastDurations(forecastGrouped[key]),
    })
  );
  return merged;
}

export function mergeSummaries(
  summary: UserSummaryDefaultPayload | undefined,
  forecastSummary: UserSummaryForecastPayload | undefined = undefined
): RawUserSummary {
  return {
    access_all_hours: false,
    total_logged_percent: 0,
    weekly_capacity: durationFromHours(0),
    ...baseDurations,
    ...massageDefaultDurations(summary),
    ...massageForecastDurations(forecastSummary),
    by_days: mergeByDays(summary?.by_days ?? [], forecastSummary?.by_days ?? []),
  };
}

function mergeByDays(
  defaultDays: UserSummaryDefaultPayload['by_days'],
  forecastDays: UserSummaryForecastPayload['by_days']
): Array<DaySummary> {
  const grouped = keyBy(defaultDays, d => d.day);
  const forecastGrouped = keyBy(forecastDays, d => d.day);
  const keys = uniq(Object.keys(grouped).concat(Object.keys(forecastGrouped)));
  const merged = keys.map(
    (key): DaySummary => ({
      ...baseDurations,
      ...massageDefaultDurations(grouped[key]),
      ...massageForecastDurations(forecastGrouped[key]),
    })
  );

  return merged;
}

type DefaultPayloadDurations = z.infer<typeof defaultDurationsSchema>;
function massageDefaultDurations<T extends DefaultPayloadDurations>(
  group: T | undefined
): undefined | (Omit<T, 'estimated_duration'> & { planned_duration: Duration }) {
  const planned_billable = group?.billable_estimated_duration?.total_hours ?? 0;
  const planned_non_billable = group?.non_billable_estimated_duration?.total_hours ?? 0;
  return {
    ...omit(group, 'estimated_duration'),
    // We rename and combine this value for simplicity
    planned_duration: durationFromHours(planned_billable + planned_non_billable),
  };
}

type ForecastPayloadDurations = z.infer<typeof forecastDurationsSchema>;
function massageForecastDurations<T extends ForecastPayloadDurations>(
  group: T | undefined
): Omit<
  T,
  'planned_duration' | 'logged_duration' | 'estimated_duration' | 'assigned_task_duration'
> & { tasked_duration?: Duration } {
  const assignedTaskDuration = group?.assigned_task_duration;

  return {
    ...omit(
      group,
      'planned_duration',
      'logged_duration',
      'estimated_duration',
      'assigned_task_duration'
    ),
    tasked_duration: assignedTaskDuration,
  };
}
